"use client";

import { useState, useRef } from "react";
import {
  FileText,
  Upload,
  X,
  Plus,
  Trash2,
  Loader2,
  Mail,
  Calendar,
  AlignLeft,
  Palette,
  Tag,
  ScrollText,
  MapPin,
} from "lucide-react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import toast from "react-hot-toast";

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface InvoiceConfig {
  logoURL: string | null;
  recipientEmail: string;
  amount: number;
  invoiceNumber: string;
  dueDate: string;
  title: string;
  description: string;
  lineItems: InvoiceLineItem[];
  currency: "USD";
  brandColor: string;
  discountAmount: number;
  terms: string;
  senderAddress: string;
}

interface InvoiceEditorProps {
  config: InvoiceConfig;
  onChange: (config: InvoiceConfig) => void;
  walletAddress: string;
  onSave: () => void;
  saving: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function computeSubtotal(config: InvoiceConfig): number {
  if (config.lineItems.length === 0) return config.amount;
  return config.lineItems.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0
  );
}

export function computeInvoiceTotal(config: InvoiceConfig): number {
  const subtotal = computeSubtotal(config);
  const discount = config.discountAmount > 0 ? config.discountAmount : 0;
  return Math.max(0, subtotal - discount);
}

export default function InvoiceEditor({
  config,
  onChange,
  walletAddress,
  onSave,
  saving,
}: InvoiceEditorProps) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const updateField = <K extends keyof InvoiceConfig>(
    field: K,
    value: InvoiceConfig[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const path = `invoice-logos/${walletAddress}/${Date.now()}-${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const url = await getDownloadURL(storageRef);
      updateField("logoURL", url);
      toast.success("Logo uploaded");
    } catch (err) {
      console.error("Logo upload error:", err);
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const removeLogo = () => {
    updateField("logoURL", null);
    toast.success("Logo removed");
  };

  const addLineItem = () => {
    const newItem: InvoiceLineItem = {
      id: Date.now().toString(),
      description: "",
      quantity: 1,
      unitPrice: 0,
    };
    updateField("lineItems", [...config.lineItems, newItem]);
  };

  const updateLineItem = (
    id: string,
    field: keyof Omit<InvoiceLineItem, "id">,
    value: string | number
  ) => {
    const updated = config.lineItems.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    updateField("lineItems", updated);
  };

  const removeLineItem = (id: string) => {
    updateField(
      "lineItems",
      config.lineItems.filter((item) => item.id !== id)
    );
  };

  const subtotal = computeSubtotal(config);
  const computedTotal = computeInvoiceTotal(config);

  const BRAND_COLORS = [
    { name: "Indigo", value: "#6366f1" },
    { name: "Blue", value: "#3b82f6" },
    { name: "Green", value: "#61CE70" },
    { name: "Rose", value: "#f43f5e" },
    { name: "Amber", value: "#f59e0b" },
    { name: "Purple", value: "#a855f7" },
    { name: "Teal", value: "#14b8a6" },
    { name: "Orange", value: "#f97316" },
  ];

  return (
    <div className="space-y-5">
      {/* Logo Upload */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <FileText size={16} />
          Business Logo
        </label>
        {config.logoURL ? (
          <div className="relative inline-block">
            <img
              src={config.logoURL}
              alt="Logo"
              className="h-16 object-contain rounded-lg border border-gray-700/50 bg-white/5 p-2"
            />
            <button
              type="button"
              onClick={removeLogo}
              className="absolute -top-2 -right-2 p-1 bg-red-500/80 rounded-full text-white hover:bg-red-600 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            disabled={uploadingLogo}
            className="border-2 border-dashed border-gray-700/50 rounded-xl px-6 py-4 text-center hover:border-[#61CE70]/50 hover:bg-[#61CE70]/5 transition-all disabled:opacity-50"
          >
            {uploadingLogo ? (
              <Loader2 size={20} className="mx-auto text-[#61CE70] animate-spin mb-1" />
            ) : (
              <Upload size={20} className="mx-auto text-gray-500 mb-1" />
            )}
            <p className="text-gray-400 text-sm">
              {uploadingLogo ? "Uploading..." : "Upload logo"}
            </p>
          </button>
        )}
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          className="hidden"
        />
      </div>

      {/* Brand Color */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <Palette size={16} />
          Invoice Color
        </label>
        <div className="flex flex-wrap gap-2">
          {BRAND_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => updateField("brandColor", c.value)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                config.brandColor === c.value
                  ? "border-white scale-110 shadow-lg"
                  : "border-gray-700/50 hover:border-gray-500"
              }`}
              style={{ backgroundColor: c.value }}
              title={c.name}
            />
          ))}
          <label className="w-8 h-8 rounded-full border-2 border-dashed border-gray-600 cursor-pointer flex items-center justify-center hover:border-gray-400 transition-colors overflow-hidden relative" title="Custom color">
            <Plus size={14} className="text-gray-500" />
            <input
              type="color"
              value={config.brandColor || "#6366f1"}
              onChange={(e) => updateField("brandColor", e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </label>
        </div>
      </div>

      {/* Due Date */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <Calendar size={16} />
          Due Date
        </label>
        <input
          type="date"
          value={config.dueDate}
          onChange={(e) => updateField("dueDate", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all [color-scheme:dark]"
        />
      </div>

      {/* Sender Address */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <MapPin size={16} />
          Your Business Address <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <textarea
          value={config.senderAddress}
          onChange={(e) => updateField("senderAddress", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all resize-none"
          placeholder="123 Main St, City, State, ZIP"
          rows={2}
          maxLength={300}
        />
      </div>

      {/* Recipient Email */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <Mail size={16} />
          Recipient Email
        </label>
        <input
          type="email"
          value={config.recipientEmail}
          onChange={(e) => updateField("recipientEmail", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
          placeholder="client@example.com"
        />
      </div>

      {/* Title */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <FileText size={16} />
          Invoice Title
        </label>
        <input
          type="text"
          value={config.title}
          onChange={(e) => updateField("title", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
          placeholder="Web Development Services"
          maxLength={200}
        />
      </div>

      {/* Line Items */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <AlignLeft size={16} />
          Line Items
        </label>

        {config.lineItems.length > 0 && (
          <div className="space-y-3 mb-3">
            {/* Header */}
            <div className="grid grid-cols-[1fr_60px_80px_70px_32px] gap-2 text-xs text-gray-500 px-1">
              <span>Description</span>
              <span className="text-center">Qty</span>
              <span className="text-center">Price</span>
              <span className="text-right">Total</span>
              <span></span>
            </div>

            {config.lineItems.map((item) => (
              <div
                key={item.id}
                className="grid grid-cols-[1fr_60px_80px_70px_32px] gap-2 items-center"
              >
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) =>
                    updateLineItem(item.id, "description", e.target.value)
                  }
                  className="px-3 py-2 bg-gray-800/30 border border-gray-700/50 rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/20 transition-all"
                  placeholder="Item description"
                />
                <input
                  type="number"
                  value={item.quantity}
                  onChange={(e) =>
                    updateLineItem(
                      item.id,
                      "quantity",
                      Math.max(1, parseInt(e.target.value) || 1)
                    )
                  }
                  className="px-2 py-2 bg-gray-800/30 border border-gray-700/50 rounded-lg text-white text-sm text-center focus:border-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/20 transition-all"
                  min={1}
                />
                <input
                  type="number"
                  value={item.unitPrice}
                  onChange={(e) =>
                    updateLineItem(
                      item.id,
                      "unitPrice",
                      Math.max(0, parseFloat(e.target.value) || 0)
                    )
                  }
                  className="px-2 py-2 bg-gray-800/30 border border-gray-700/50 rounded-lg text-white text-sm text-center focus:border-[#61CE70] focus:ring-1 focus:ring-[#61CE70]/20 transition-all"
                  min={0}
                  step={0.01}
                />
                <span className="text-sm text-gray-300 text-right font-medium">
                  ${(item.quantity * item.unitPrice).toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => removeLineItem(item.id)}
                  className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={addLineItem}
          className="flex items-center gap-2 text-sm text-[#61CE70] hover:text-[#72D981] font-medium transition-colors"
        >
          <Plus size={16} />
          Add Line Item
        </button>
      </div>

      {/* Direct Amount (when no line items) */}
      {config.lineItems.length === 0 && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
            Amount (USD)
          </label>
          <input
            type="number"
            value={config.amount}
            onChange={(e) =>
              updateField("amount", Math.max(0, parseFloat(e.target.value) || 0))
            }
            className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
            placeholder="0.00"
            min={0}
            step={0.01}
          />
        </div>
      )}

      {/* Discount */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <Tag size={16} />
          Discount ($)
        </label>
        <input
          type="number"
          value={config.discountAmount}
          onChange={(e) =>
            updateField("discountAmount", Math.max(0, parseFloat(e.target.value) || 0))
          }
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
          placeholder="0.00"
          min={0}
          step={0.01}
        />
      </div>

      {/* Total Breakdown */}
      <div className="rounded-xl border border-gray-700/50 bg-gray-800/20 overflow-hidden">
        <div className="px-4 py-2.5 flex items-center justify-between text-sm">
          <span className="text-gray-400">Subtotal</span>
          <span className="text-gray-300 font-medium">${subtotal.toFixed(2)}</span>
        </div>
        {config.discountAmount > 0 && (
          <div className="px-4 py-2.5 flex items-center justify-between text-sm border-t border-gray-700/30">
            <span className="text-gray-400">Discount</span>
            <span className="text-green-400 font-medium">-${config.discountAmount.toFixed(2)}</span>
          </div>
        )}
        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-700/50" style={{ backgroundColor: `${config.brandColor || "#6366f1"}10` }}>
          <span className="text-sm font-semibold text-gray-200">Total</span>
          <span className="text-lg font-bold" style={{ color: config.brandColor || "#6366f1" }}>
            ${computedTotal.toFixed(2)} USD
          </span>
        </div>
      </div>

      {/* Description / Notes */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <AlignLeft size={16} />
          Notes / Memo
        </label>
        <textarea
          value={config.description}
          onChange={(e) => updateField("description", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all resize-none"
          placeholder="Payment terms, additional notes..."
          rows={3}
          maxLength={1000}
        />
        <p className="text-gray-500 text-xs mt-1 text-right">
          {config.description.length}/1000
        </p>
      </div>

      {/* Payment Terms */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <ScrollText size={16} />
          Payment Terms <span className="text-gray-500 text-xs">(optional)</span>
        </label>
        <textarea
          value={config.terms}
          onChange={(e) => updateField("terms", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all resize-none"
          placeholder="e.g., Payment is due within 30 days of receipt..."
          rows={2}
          maxLength={500}
        />
      </div>

      {/* Save Button */}
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-3 rounded-xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" />
            Saving...
          </span>
        ) : (
          "Save & Generate Link"
        )}
      </button>
    </div>
  );
}
